import React, { useState } from 'react';
import {
  Box, Button, Checkbox, CircularProgress, FormControlLabel, Stack, TextField, Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../layout/PageHeader';
import PostData from '../../../services/PostData';
import SnackbarEx from '../../Helpers/SnackbarEx';
import AddIngredient from '../specialtyItemTab/bulkCustomItem/AddIngredients';
import { tokens } from '../../../theme';

/**
 * Basic catalog/product create — local inventory item (made or whole).
 */
export default function AddCatalogItem() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [snack, setSnack] = useState({ color: '', message: '', open: false });
  const [ingredients, setIngredients] = useState([]);
  const [form, setForm] = useState({
    title: '',
    description: '',
    price: '',
    sku: '',
    availForSale: 0,
    requiresSupplies: false,
    trackInventory: true,
    restockThreshold: 0,
    category: 'Uncategorized',
  });

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleIngredients = (value) => {
    if (Array.isArray(value)) setIngredients(value);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!form.title.trim()) {
      setSnack({ color: 'error', message: 'Product name is required.', open: true });
      return;
    }
    if (form.price === '' || Number.isNaN(Number(form.price))) {
      setSnack({ color: 'error', message: 'Sale price is required.', open: true });
      return;
    }

    setSubmitting(true);
    const payload = {
      ...form,
      price: Number(form.price),
      availForSale: Number(form.availForSale) || 0,
      restockThreshold: Number(form.restockThreshold) || 0,
      requiresSupplies: Boolean(form.requiresSupplies),
      trackInventory: Boolean(form.trackInventory),
      ingredients: form.requiresSupplies ? ingredients : [],
      childData: form.requiresSupplies ? ingredients : [],
      variants: [],
      salesChannels: [],
    };

    const { success, error } = await PostData(payload, 'api/create-product', null);
    setSubmitting(false);

    if (success) {
      setSnack({ color: 'success', message: 'Product created.', open: true });
      setTimeout(() => navigate('/inventory'), 600);
    } else {
      setSnack({
        color: 'error',
        message: error?.response?.data?.error?.message || 'Could not create product.',
        open: true,
      });
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
      <PageHeader
        title="Create product"
        subtitle="Add a made or whole catalog item to local inventory"
        actions={
          <Button variant="outlined" onClick={() => navigate('/inventory')}>
            Back to inventory
          </Button>
        }
      />

      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          maxWidth: 720,
          mx: 'auto',
          width: '100%',
          px: { xs: 2, md: 3 },
          py: 3,
        }}
      >
        <Stack spacing={2.5}>
          <TextField
            label="Product name"
            name="title"
            required
            fullWidth
            value={form.title}
            onChange={(e) => setField('title', e.target.value)}
          />
          <TextField
            label="Description"
            name="description"
            fullWidth
            multiline
            minRows={3}
            value={form.description}
            onChange={(e) => setField('description', e.target.value)}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <TextField
              label="Sale price"
              name="price"
              type="number"
              required
              fullWidth
              inputProps={{ min: 0, step: '0.01' }}
              value={form.price}
              onChange={(e) => setField('price', e.target.value)}
            />
            <TextField
              label="SKU"
              name="sku"
              fullWidth
              value={form.sku}
              onChange={(e) => setField('sku', e.target.value)}
            />
            <TextField
              label="On hand"
              name="availForSale"
              type="number"
              fullWidth
              inputProps={{ min: 0 }}
              value={form.availForSale}
              onChange={(e) => setField('availForSale', e.target.value)}
            />
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center">
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.requiresSupplies}
                  onChange={(e) => setField('requiresSupplies', e.target.checked)}
                />
              }
              label="Made from raw ingredients"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.trackInventory}
                  onChange={(e) => setField('trackInventory', e.target.checked)}
                />
              }
              label="Track inventory"
            />
            <TextField
              label="Restock threshold"
              type="number"
              size="small"
              sx={{ width: 160 }}
              value={form.restockThreshold}
              onChange={(e) => setField('restockThreshold', e.target.value)}
            />
          </Stack>

          {form.requiresSupplies && (
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                border: `1px solid ${tokens.border}`,
                backgroundColor: tokens.bg,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                Recipe ingredients
              </Typography>
              <AddIngredient onDataChange={handleIngredients} />
            </Box>
          )}

          <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ pt: 1 }}>
            <Button variant="outlined" onClick={() => navigate('/inventory')} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={submitting}>
              {submitting ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Create product'}
            </Button>
          </Stack>
        </Stack>
      </Box>

      {snack.open && (
        <SnackbarEx
          color={snack.color}
          message={snack.message}
          open={snack.open}
          close={() => setSnack({ color: '', message: '', open: false })}
        />
      )}
    </Box>
  );
}
